import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { PageHeader, StatusBadge, LoadingSpinner, EmptyState } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { sitesAPI, clientsAPI } from '../utils/api';
import { formatNumber, formatDecimal, formatDateTime } from '../utils/helpers';
import { Plus, Building2, MapPin, Edit, Archive, AlertTriangle, Settings, Trash2, ArchiveRestore } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { DemoModeToggle } from '../components/DemoModeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SitesManagementPage = () => {
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSite, setShowAddSite] = useState(false);
  const [showEditSite, setShowEditSite] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [archivingSite, setArchivingSite] = useState(null);
  const [restoringSite, setRestoringSite] = useState(null);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [deletingSite, setDeletingSite] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    client_id: '',
    location: '',
    latitude: '',
    longitude: '',
    installed_power_kw: '',
    installed_capacity_kwh: '',
    status: 'offline',
    commissioned_date: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  useEffect(() => {
    fetchData();
  }, [showArchived]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sitesRes, clientsRes] = await Promise.all([
        sitesAPI.getAll({ include_archived: showArchived }),
        clientsAPI.getAll(),
      ]);
      setSites(sitesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to fetch sites:', error);
      toast.error('Failed to load sites');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.type === 'number' ? parseFloat(e.target.value) : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleEdit = (site, e) => {
    e.stopPropagation();
    setEditingSite(site);
    setFormData({
      name: site.name,
      client_id: site.client_id,
      location: site.location,
      latitude: site.latitude || '',
      longitude: site.longitude || '',
      installed_power_kw: site.installed_power_kw,
      installed_capacity_kwh: site.installed_capacity_kwh,
      status: site.status,
      commissioned_date: site.commissioned_date || '',
    });
    setShowEditSite(true);
  };

  const handleArchiveClick = (site, e) => {
    e.stopPropagation();
    setArchivingSite(site);
    setShowArchiveConfirm(true);
  };

  const handleArchiveConfirm = async () => {
    if (!archivingSite) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/sites/${archivingSite.site_id}/archive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Site archived successfully');
      setShowArchiveConfirm(false);
      setArchivingSite(null);
      fetchData();
    } catch (error) {
      console.error('Failed to archive site:', error);
      toast.error(error.response?.data?.detail || 'Failed to archive site');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestoreClick = (site, e) => {
    e.stopPropagation();
    setRestoringSite(site);
    setShowRestoreConfirm(true);
  };

  const handleRestoreConfirm = async () => {
    if (!restoringSite) return;
    setSubmitting(true);
    try {
      await sitesAPI.restore(restoringSite.site_id);
      toast.success('Site restored');
      setShowRestoreConfirm(false);
      setRestoringSite(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore site');
    } finally { setSubmitting(false); }
  };

  const handleDeleteClick = (site, e) => {
    e.stopPropagation();
    setDeletingSite(site);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingSite) return;
    setSubmitting(true);
    try {
      const res = await sitesAPI.delete(deletingSite.site_id);
      toast.success(
        `Site deleted · ${res.data?.assets_deleted || 0} asset(s) removed`,
      );
      setShowDeleteConfirm(false);
      setDeletingSite(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete site');
    } finally { setSubmitting(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (!formData.client_id) {
        toast.error('Please select a client');
        setSubmitting(false);
        return;
      }
      // Convert string numbers to floats
      const payload = {
        ...formData,
        installed_power_kw: parseFloat(formData.installed_power_kw),
        installed_capacity_kwh: parseFloat(formData.installed_capacity_kwh),
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      };

      if (editingSite) {
        // Update existing site
        const token = localStorage.getItem('token');
        await axios.put(
          `${BACKEND_URL}/api/sites/${editingSite.site_id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Site updated successfully!');
        setShowEditSite(false);
        setEditingSite(null);
      } else {
        // Create new site
        await sitesAPI.create(payload);
        toast.success('Site created successfully!');
        setShowAddSite(false);
      }
      
      setFormData({
        name: '',
        client_id: '',
        location: '',
        latitude: '',
        longitude: '',
        installed_power_kw: '',
        installed_capacity_kwh: '',
        status: 'offline',
        commissioned_date: '',
      });
      fetchData();
    } catch (error) {
      console.error('Failed to save site:', error);
      toast.error(error.response?.data?.detail || 'Failed to save site');
    } finally {
      setSubmitting(false);
    }
  };

  const getClientName = (clientId) => {
    return clients.find((c) => c.client_id === clientId)?.name || 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-8">
          <PageHeader
            title="Sites Management"
            description="Manage and monitor all BESS sites"
            action={
              isAdmin && (
                <Button
                  onClick={() => setShowAddSite(true)}
                  data-testid="add-site-button"
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Site
                </Button>
              )
            }
          />

          <div className="flex justify-between items-center mb-4">
            {isAdmin && (
              <label
                className="flex items-center gap-1.5 text-xs text-slate-600 border border-slate-200 rounded px-2.5 py-1.5 cursor-pointer hover:bg-slate-50 bg-white"
                data-testid="toggle-show-archived"
              >
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="w-3.5 h-3.5"
                />
                Show archived
              </label>
            )}
            <DemoModeToggle onToggle={fetchData} />
          </div>

          {sites.length === 0 ? (
            <EmptyState
              icon={Building2}
              title="No sites found"
              description="Get started by adding your first BESS site"
              action={
                isAdmin && (
                  <Button onClick={() => setShowAddSite(true)} className="bg-slate-900 hover:bg-slate-800">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Site
                  </Button>
                )
              }
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {sites.map((site) => (
                <div
                  key={site.site_id}
                  onClick={() => navigate(`/sites/${site.site_id}`)}
                  data-testid={`site-card-${site.site_id}`}
                  className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-slate-100 rounded-sm flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-slate-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900">{site.name}</h3>
                          {site.archived && (
                            <span
                              className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                              data-testid={`archived-pill-${site.site_id}`}
                            >
                              Archived
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{getClientName(site.client_id)}</p>
                      </div>
                    </div>
                    <StatusBadge status={site.live_status || 'offline'} />
                  </div>

                  <p className="text-sm text-slate-600 flex items-center mb-4">
                    <MapPin className="w-3 h-3 mr-1" />
                    {site.location}
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Power</p>
                      <p className="text-lg font-bold text-slate-900">
                        {formatDecimal(site.installed_power_kw, 0)} kW
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Capacity</p>
                      <p className="text-lg font-bold text-slate-900">
                        {formatDecimal(site.installed_capacity_kwh, 0)} kWh
                      </p>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                    <p className="text-xs text-slate-500">
                      Last data: {site.last_data_at ? formatDateTime(site.last_data_at) : 'Never'}
                    </p>
                    {isAdmin && (
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => handleEdit(site, e)}
                          data-testid={`edit-site-${site.site_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/sites/${site.site_id}/onboarding`);
                          }}
                          title="Manage Assets & Contracts"
                          data-testid={`onboard-site-${site.site_id}`}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        {site.archived ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => handleRestoreClick(site, e)}
                            data-testid={`restore-site-${site.site_id}`}
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            title="Restore site"
                          >
                            <ArchiveRestore className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => handleArchiveClick(site, e)}
                            data-testid={`archive-site-${site.site_id}`}
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            title="Archive site"
                          >
                            <Archive className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => handleDeleteClick(site, e)}
                          data-testid={`delete-site-${site.site_id}`}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Delete site permanently"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Site Dialog */}
      <Dialog open={showAddSite || showEditSite} onOpenChange={(open) => {
        if (!open) {
          setShowAddSite(false);
          setShowEditSite(false);
          setEditingSite(null);
          setFormData({
            name: '',
            client_id: '',
            location: '',
            latitude: '',
            longitude: '',
            installed_power_kw: '',
            installed_capacity_kwh: '',
            status: 'offline',
            commissioned_date: '',
          });
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSite ? 'Edit BESS Site' : 'Add New BESS Site'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Site Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Bangalore Solar Farm"
                  required
                  className="mt-2"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="client_id">Client *</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={(value) => handleSelectChange('client_id', value)}
                  required
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.client_id} value={client.client_id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="e.g., Bangalore, Karnataka"
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="latitude">Latitude (Optional)</Label>
                <Input
                  id="latitude"
                  name="latitude"
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={handleInputChange}
                  placeholder="e.g., 12.9716"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="longitude">Longitude (Optional)</Label>
                <Input
                  id="longitude"
                  name="longitude"
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={handleInputChange}
                  placeholder="e.g., 77.5946"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="installed_power_kw">Installed Power (kW) *</Label>
                <Input
                  id="installed_power_kw"
                  name="installed_power_kw"
                  type="number"
                  step="any"
                  value={formData.installed_power_kw}
                  onChange={handleInputChange}
                  placeholder="e.g., 500"
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="installed_capacity_kwh">Installed Capacity (kWh) *</Label>
                <Input
                  id="installed_capacity_kwh"
                  name="installed_capacity_kwh"
                  type="number"
                  step="any"
                  value={formData.installed_capacity_kwh}
                  onChange={handleInputChange}
                  placeholder="e.g., 2000"
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="status">Initial Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => handleSelectChange('status', value)}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="offline">Offline</SelectItem>
                    <SelectItem value="partial_offline">Partial Offline</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="commissioned_date">Commissioned Date (Optional)</Label>
                <Input
                  id="commissioned_date"
                  name="commissioned_date"
                  type="date"
                  value={formData.commissioned_date}
                  onChange={handleInputChange}
                  className="mt-2"
                />
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
              <p className="text-sm text-blue-900">
                <strong>Note:</strong> After creating the site, you can assign client users access to it from the Users page.
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddSite(false);
                  setShowEditSite(false);
                  setEditingSite(null);
                }}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-slate-900 hover:bg-slate-800"
              >
                {submitting ? (editingSite ? 'Updating...' : 'Creating...') : (editingSite ? 'Update Site' : 'Create Site')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={showArchiveConfirm} onOpenChange={setShowArchiveConfirm}>
        <DialogContent data-testid="archive-site-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <span>Archive Site?</span>
            </DialogTitle>
            <DialogDescription className="pt-4">
              Are you sure you want to archive <strong>{archivingSite?.name}</strong>?
              <br /><br />
              <span className="text-amber-600">This will hide the site from the main list but preserve all data and telemetry records. You can restore it any time.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={() => { setShowArchiveConfirm(false); setArchivingSite(null); }}
              disabled={submitting}
              data-testid="archive-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleArchiveConfirm}
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="archive-confirm-btn"
            >
              {submitting ? 'Archiving...' : 'Archive Site'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Restore confirm */}
      <Dialog open={showRestoreConfirm} onOpenChange={setShowRestoreConfirm}>
        <DialogContent data-testid="restore-site-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <ArchiveRestore className="w-5 h-5 text-emerald-600" />
              <span>Restore Site?</span>
            </DialogTitle>
            <DialogDescription className="pt-4">
              Bring <strong>{restoringSite?.name}</strong> back to the active fleet? Live monitoring resumes immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={() => { setShowRestoreConfirm(false); setRestoringSite(null); }}
              disabled={submitting}
              data-testid="restore-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRestoreConfirm}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="restore-confirm-btn"
            >
              {submitting ? 'Restoring...' : 'Restore Site'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete permanently confirm */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent data-testid="delete-site-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-rose-700">
              <Trash2 className="w-5 h-5" />
              <span>Delete site permanently?</span>
            </DialogTitle>
            <DialogDescription className="pt-4">
              This will <strong>permanently remove</strong> <em>{deletingSite?.name}</em> and its BESS assets.
              <br /><br />
              Historical outage events, alarms, telemetry and JEM mappings are kept for audit but marked as orphaned.
              <br />
              <span className="text-rose-600 font-medium">This action cannot be undone.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={() => { setShowDeleteConfirm(false); setDeletingSite(null); }}
              disabled={submitting}
              data-testid="delete-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={submitting}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="delete-confirm-btn"
            >
              {submitting ? 'Deleting...' : 'Yes, delete permanently'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
